import { playerAnalyticsModel as player } from '$/player'
import { ROUTE_NAME } from '@/constants'
import {
  AnalyticsContentType,
  PlayerEventTypeOVP,
  type IContentInfoOVP,
  type VideoSourceInfoOVP,
} from '@setplex/tria-api'
import { attach, createStore, sample } from 'effector'
import { and } from 'patronum'
import { activeProgramModel } from '~/features/epg/active-program'
import { $route } from '~/processes/navigation'
import type { SendEventParams } from '~/shared/analytix'
import { F } from '~/shared/helpers'
import * as epgAnalyticsWidgetModel from '~/widgets/epg/analytics'
import { tvshowPlayerWidgetModel } from '~/widgets/player/tvshows'
import { logErrorFx } from '../lib'
import { $readyAllInfo } from '../session/model'
import { $analytix, $analytixEnabled } from '../wrapper'
import {
  getPlayStartCategoryTitle,
  getProgramOrEpisodeId,
  getProgramOrEpisodeTitle,
  mapCurrentProgramToEpgAnalytics,
  type EpgPlayingAnalyticsData,
  type PlayerStreamVariant,
} from './lib'

const $playerAnalytixCanSend = and($readyAllInfo, $analytixEnabled)

export const $epgPlayingData = createStore<EpgPlayingAnalyticsData | null>(null)

sample({
  source: activeProgramModel.$currentProgram,
  fn: mapCurrentProgramToEpgAnalytics,
  target: $epgPlayingData,
})

const streamCore = (d: VideoSourceInfoOVP, variant: PlayerStreamVariant) => ({
  contentId: String(d.contentId ?? ''),
  contentType: String(d.contentType ?? ''),
  contentUrl: String(d.contentUrl ?? ''),
  audioLanguage: String(d.audioLanguage ?? ''),
  subtitlesLanguage: String(d.textLanguage ?? ''),
  contentLength: String(d.duration ?? ''),
  creatorId: String(d.publisherId ?? ''),
  playerSessionId: String(d.playerSessionId ?? ''),
  programOrEpisodeId: getProgramOrEpisodeId(d, variant),
  programOrEpisodeTitle: getProgramOrEpisodeTitle(d, variant),
  region: String(d.regionTitle ?? ''),
  watchId: String(d.watchId ?? ''),
  fullscreenType: String(d.fullscreenType ?? ''),
  cmcdSessionId: String(d.cmcdSessionId ?? ''),
})

const toPlayStart = (d: VideoSourceInfoOVP): SendEventParams.PlayStart => ({
  ...streamCore(d, 'playStart'),
  startSecond: String(d.startedTime ?? ''),
  category: String(d.categoryTitle ?? ''),
  playbackTrigger: String(d.playbackStartTrigger ?? ''),
})

const toPlayStop = (d: VideoSourceInfoOVP): SendEventParams.PlayStop => ({
  ...streamCore(d, 'playStop'),
  stopSecond: String(d.stoppedTime ?? ''),
})

const toPlayerOpen = (d: IContentInfoOVP): SendEventParams.PlayerOpen => ({
  contentId: String(d.contentId ?? ''),
  contentType: String(d.contentType ?? ''),
  contentUrl: String(d.contentUrl ?? ''),
  contentTitle: String(d.contentTitle ?? ''),
  openFrom: String(d.contentOpenFrom ?? ''),
  errorCode: String(d.contentErrorCode ?? ''),
  creatorId: String(d.publisherId ?? ''),
  playerSessionId: String(d.playerSessionId ?? ''),
  region: String(d.regionTitle ?? ''),
  fullscreenType: String(d.fullscreenType ?? ''),
})

const toPlayerWatching = (
  d: VideoSourceInfoOVP
): SendEventParams.PlayerWatching => ({
  ...streamCore(d, 'playerWatching'),
  playerSecond: String(d.stoppedTime ?? ''),
})

const toPlayerEvent = (d: VideoSourceInfoOVP): SendEventParams.PlayerEvent => ({
  ...streamCore(d, 'playerEvent'),
  playerSecond: String(d.startedTime ?? ''),
  playerEventType: String(d.playerEventType ?? ''),
})

const toPlayError = (d: VideoSourceInfoOVP): SendEventParams.PlayError => ({
  contentId: String(d.contentId ?? ''),
  contentType: String(d.contentType ?? ''),
  contentUrl: String(d.contentUrl ?? ''),
  errorCode: String(d.errorCode ?? ''),
  UIErrorCode: String(d.errorCodeUI ?? ''),
  errorMessage: String(d.errorMessage ?? ''),
  creatorId: String(d.publisherId ?? ''),
  playerSessionId: String(d.playerSessionId ?? ''),
  region: String(d.regionTitle ?? ''),
  watchId: String(d.watchId ?? ''),
  fullscreenType: String(d.fullscreenType ?? ''),
  cmcdSessionId: String(d.cmcdSessionId ?? ''),
})

export const sendPlayStartFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.PlayStart) =>
    analytix.sendPlayStartEvent(params),
})

export const sendPlayStopFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.PlayStop) =>
    analytix.sendPlayStopEvent(params),
})

export const sendPlayerOpenFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.PlayerOpen) =>
    analytix.sendPlayerOpenEvent(params),
})

export const sendPlayerWatchingFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.PlayerWatching) =>
    analytix.sendPlayerWatchingEvent(params),
})

export const sendPlayerEventFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.PlayerEvent) =>
    analytix.sendPlayerEvent(params),
})

export const sendPlayErrorFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.PlayError) =>
    analytix.sendPlayErrorEvent(params),
})

for (const fx of [
  sendPlayStartFx,
  sendPlayStopFx,
  sendPlayerOpenFx,
  sendPlayerWatchingFx,
  sendPlayerEventFx,
  sendPlayErrorFx,
]) {
  sample({
    clock: fx.failData,
    fn: (e) => e,
    target: logErrorFx,
  })
}

sample({
  clock: sendPlayStopFx,
  fn: () => undefined,
  target: player.playStopAnalytixDispatched,
})

sample({
  clock: player.playError,
  source: { canSend: $playerAnalytixCanSend },
  filter: ({ canSend }) => Boolean(canSend),
  fn: (_, data) => toPlayError(data as VideoSourceInfoOVP),
  target: sendPlayErrorFx,
})

// play_start — same clocks/filters as legacy OVP wiring in `processes/analytics/model`
sample({
  clock: [
    player.$playerData,
    $epgPlayingData,
    tvshowPlayerWidgetModel.$episodesData,
  ],
  source: {
    playerData: player.$playerData,
    epgData: $epgPlayingData,
    canSend: $playerAnalytixCanSend,
    playbackTrigger: player.$playbackTrigerData,
    category: epgAnalyticsWidgetModel.$activeEpgCategory,
    episodesData: tvshowPlayerWidgetModel.$episodesData,
    route: $route,
  },
  filter: ({ playerData, epgData, canSend, episodesData }) => {
    const ok = Boolean(playerData) && Boolean(canSend)
    switch (playerData?.contentType) {
      case AnalyticsContentType.live:
      case AnalyticsContentType.liveRewind:
      case AnalyticsContentType.catchup:
        return ok && Boolean(epgData)
      case AnalyticsContentType.tvshow:
        return ok && Boolean(episodesData)
      default:
        return ok
    }
  },
  fn: ({
    playerData,
    epgData,
    playbackTrigger,
    category,
    episodesData,
    route,
  }) => {
    const isLive = playerData?.contentType === AnalyticsContentType.live
    const isLiveRewind =
      playerData?.contentType === AnalyticsContentType.liveRewind
    const isCatchup = playerData?.contentType === AnalyticsContentType.catchup
    const isTvShow = playerData?.contentType === AnalyticsContentType.tvshow
    const isEpgPage = route?.routeName === ROUTE_NAME.LIVE_CATEGORIES

    const dataToSend: VideoSourceInfoOVP = {
      ...playerData,
      playbackStartTrigger: playbackTrigger,
    }

    if (isTvShow) {
      return toPlayStart({ ...dataToSend, ...episodesData })
    }

    return toPlayStart(
      isLive || isCatchup || isLiveRewind
        ? {
            ...dataToSend,
            ...epgData,
            categoryTitle: getPlayStartCategoryTitle(isEpgPage, category),
          }
        : dataToSend
    )
  },
  target: [sendPlayStartFx, player.resetPlayerData],
})

sample({
  clock: player.stop,
  source: {
    canSend: $playerAnalytixCanSend,
    episodesData: tvshowPlayerWidgetModel.$episodesData,
  },
  filter: ({ canSend, episodesData }, playerData) => {
    switch (playerData?.contentType) {
      case AnalyticsContentType.tvshow:
        return Boolean(episodesData) && Boolean(canSend)
      default:
        return Boolean(canSend)
    }
  },
  fn: ({ episodesData }, playerData) => {
    switch (playerData?.contentType) {
      case AnalyticsContentType.tvshow:
        return toPlayStop({
          ...playerData,
          ...episodesData,
        } as VideoSourceInfoOVP)
      default:
        return toPlayStop(playerData as VideoSourceInfoOVP)
    }
  },
  target: sendPlayStopFx,
})

sample({
  clock: player.playerOpen,
  source: {
    isPlayerJustOpened: player.$isPlayerJustOpened,
    canSend: $playerAnalytixCanSend,
  },
  filter: ({ isPlayerJustOpened, canSend }) =>
    Boolean(isPlayerJustOpened) && Boolean(canSend),
  fn: (_, data) => {
    const isLiveContent = data.contentType === AnalyticsContentType.live
    return toPlayerOpen({
      ...data,
      regionTitle: isLiveContent ? data?.regionTitle : '',
    })
  },
  target: sendPlayerOpenFx,
})

sample({
  clock: sendPlayerOpenFx,
  fn: F,
  target: player.setIsPlayerJustOpened,
})

sample({
  clock: player.playerWatching,
  source: {
    playerWatchingEventInterval: player.$playerWatchingEventInterval,
    canSend: $playerAnalytixCanSend,
    epgData: $epgPlayingData,
    episodesData: tvshowPlayerWidgetModel.$episodesData,
  },
  filter: (
    { playerWatchingEventInterval, canSend, epgData, episodesData },
    playerData
  ) => {
    const enabled = Boolean(playerWatchingEventInterval) && Boolean(canSend)

    const isEpgContent =
      playerData?.contentType === AnalyticsContentType.live ||
      playerData?.contentType === AnalyticsContentType.liveRewind ||
      playerData?.contentType === AnalyticsContentType.catchup

    if (isEpgContent) {
      return Boolean(epgData) && enabled
    }

    if (playerData?.contentType === AnalyticsContentType.tvshow) {
      return Boolean(episodesData) && enabled
    }

    return enabled
  },
  fn: ({ epgData, episodesData }, playerData) => {
    const isEpgContent =
      playerData?.contentType === AnalyticsContentType.live ||
      playerData?.contentType === AnalyticsContentType.liveRewind ||
      playerData?.contentType === AnalyticsContentType.catchup

    if (isEpgContent) {
      return toPlayerWatching({
        ...playerData,
        ...epgData,
      } as VideoSourceInfoOVP)
    }

    if (playerData?.contentType === AnalyticsContentType.tvshow) {
      return toPlayerWatching({
        ...playerData,
        ...episodesData,
      } as VideoSourceInfoOVP)
    }

    return toPlayerWatching(playerData as VideoSourceInfoOVP)
  },
  target: sendPlayerWatchingFx,
})

sample({
  clock: player.playerEvent,
  source: {
    canSend: $playerAnalytixCanSend,
    route: $route,
    episodesData: tvshowPlayerWidgetModel.$episodesData,
  },
  filter: ({ canSend, route, episodesData }, playerData) => {
    const enabled = Boolean(canSend)

    const isEpgPage = route?.routeName === ROUTE_NAME.LIVE_CATEGORIES
    const hasFullScreenOrMinimized =
      playerData?.playerEventType === PlayerEventTypeOVP.fullScreen ||
      playerData?.playerEventType === PlayerEventTypeOVP.minimizedView

    const isLiveContent = playerData?.contentType === AnalyticsContentType.live
    const isLiveRewind =
      playerData?.contentType === AnalyticsContentType.liveRewind
    const isCatchup = playerData?.contentType === AnalyticsContentType.catchup
    const isTvShowContent =
      playerData?.contentType === AnalyticsContentType.tvshow

    const hasValidTvShowData = Boolean(episodesData)

    if (isLiveContent || isCatchup || isLiveRewind) {
      if (isEpgPage) {
        return enabled
      }
      return enabled && !hasFullScreenOrMinimized
    }

    if (!isEpgPage) {
      const hasValidData = isTvShowContent ? hasValidTvShowData : true
      return enabled && !hasFullScreenOrMinimized && hasValidData
    }

    return enabled
  },
  fn: ({ episodesData }, playerData) => {
    const isTvShow = playerData?.contentType === AnalyticsContentType.tvshow

    if (isTvShow) {
      return toPlayerEvent({
        ...playerData,
        ...episodesData,
      } as VideoSourceInfoOVP)
    }
    return toPlayerEvent(playerData as VideoSourceInfoOVP)
  },
  target: sendPlayerEventFx,
})
